import { Card, Grid, Divider, TextField } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { Loading, Notify } from 'notiflix';
import * as yup from 'yup';
import { ENDPOINT } from 'constants';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Icon from '@mui/material/Icon';

// Sample data purely so the header/footer preview has something to sit around —
// this page never changes email body content, only header/footer branding.
const PREVIEW_SUBJECT = 'Sample preview';
const PREVIEW_BODY = `Hi {firstName},

This is a sample email body so you can see exactly how the header and footer will look on every real email — including your unsaved changes below.

Best regards,
The Desteliq Team`;
const PREVIEW_VARIABLES = { firstName: 'James', email: 'james@example.com' };

const emailBrandingSchema = yup.object().shape({
  tagline: yup.string().required('Tagline is required'),
  heading: yup.string().required('Heading is required'),
  xUrl: yup.string().url('Must be a valid URL').nullable(),
  linkedinUrl: yup.string().url('Must be a valid URL').nullable(),
  facebookUrl: yup.string().url('Must be a valid URL').nullable(),
  instagramUrl: yup.string().url('Must be a valid URL').nullable(),
});

// One small reusable field for the 3 background-image uploads — shows the current/saved
// image (or a freshly-picked one before it's even uploaded), a plain file input, and a
// Remove button once an image is present.
const BackgroundImageField = ({ label, hint, previewUrl, onFileSelect, onRemove }) => (
  <MDBox mb={2}>
    <MDTypography variant="caption" fontWeight="medium" display="block" mb={0.5}>
      {label}
    </MDTypography>
    {previewUrl && (
      <MDBox
        component="img"
        src={previewUrl}
        alt={label}
        sx={{
          width: '100%',
          maxHeight: 100,
          objectFit: 'cover',
          borderRadius: 1,
          mb: 1,
          border: '1px solid',
          borderColor: 'grey.300',
        }}
      />
    )}
    <MDBox display="flex" alignItems="center" gap={1}>
      <input
        type="file"
        accept="image/*"
        onChange={(e) => e.target.files[0] && onFileSelect(e.target.files[0])}
        style={{ flex: 1 }}
      />
      {previewUrl && (
        <MDButton size="small" variant="outlined" color="error" onClick={onRemove}>
          Remove
        </MDButton>
      )}
    </MDBox>
    <MDTypography variant="caption" color="secondary" display="block" mt={0.5}>
      {hint}
    </MDTypography>
  </MDBox>
);

const EmailBranding = () => {
  const { user } = useSelector((state) => state.auth);
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isSubmitting },
  } = useForm({
    resolver: yupResolver(emailBrandingSchema),
    defaultValues: {
      tagline: '',
      heading: '',
      xUrl: '',
      linkedinUrl: '',
      facebookUrl: '',
      instagramUrl: '',
    },
  });

  // Background image uploads — each tracks the selected File (sent on Save), a preview
  // URL (the saved DO Spaces URL, or a local data: URL once a new file is picked — either
  // works directly as a preview/background-url value), and whether "Remove" was clicked.
  const [headerBgFile, setHeaderBgFile] = useState(null);
  const [bodyBgFile, setBodyBgFile] = useState(null);
  const [footerBgFile, setFooterBgFile] = useState(null);
  const [headerBgPreview, setHeaderBgPreview] = useState('');
  const [bodyBgPreview, setBodyBgPreview] = useState('');
  const [footerBgPreview, setFooterBgPreview] = useState('');
  const [headerBgRemoved, setHeaderBgRemoved] = useState(false);
  const [bodyBgRemoved, setBodyBgRemoved] = useState(false);
  const [footerBgRemoved, setFooterBgRemoved] = useState(false);

  // Banner images — full-width image sections (header banner sits right after the header,
  // footer banner sits at the end of the body, before the footer). Same file/preview/remove
  // shape as the background images above.
  const [headerBannerFile, setHeaderBannerFile] = useState(null);
  const [footerBannerFile, setFooterBannerFile] = useState(null);
  const [headerBannerPreview, setHeaderBannerPreview] = useState('');
  const [footerBannerPreview, setFooterBannerPreview] = useState('');
  const [headerBannerRemoved, setHeaderBannerRemoved] = useState(false);
  const [footerBannerRemoved, setFooterBannerRemoved] = useState(false);

  // Logo icon — the image shown inside the header's circular badge, next to the heading text.
  const [logoIconFile, setLogoIconFile] = useState(null);
  const [logoIconPreview, setLogoIconPreview] = useState('');
  const [logoIconRemoved, setLogoIconRemoved] = useState(false);

  const selectBgImage = (file, setFile, setPreview, setRemoved) => {
    setFile(file);
    setRemoved(false);
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const removeBgImage = (setFile, setPreview, setRemoved) => {
    setFile(null);
    setPreview('');
    setRemoved(true);
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  // Live preview — rendered server-side through the exact same pipeline as a real send,
  // with the CURRENT form values (even unsaved ones) layered over the saved branding, so
  // you can see the effect of an edit before committing to "Save".
  const tagline = watch('tagline');
  const heading = watch('heading');
  const xUrl = watch('xUrl');
  const linkedinUrl = watch('linkedinUrl');
  const facebookUrl = watch('facebookUrl');
  const instagramUrl = watch('instagramUrl');
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    setPreviewLoading(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await axios.post(
          `${ENDPOINT}api/v1/admin/reminder-templates/preview`,
          {
            subject: PREVIEW_SUBJECT,
            body: PREVIEW_BODY,
            variables: PREVIEW_VARIABLES,
            branding: {
              tagline,
              heading,
              xUrl,
              linkedinUrl,
              facebookUrl,
              instagramUrl,
              headerBgImage: headerBgPreview,
              bodyBgImage: bodyBgPreview,
              footerBgImage: footerBgPreview,
              headerBannerImage: headerBannerPreview,
              footerBannerImage: footerBannerPreview,
              logoIcon: logoIconPreview,
            },
          },
          { headers: { Authorization: `Bearer ${user?.accessToken}` } },
        );
        setPreviewHtml(data.data.html);
      } catch (err) {
        console.error('Failed to render branding preview:', err);
      } finally {
        setPreviewLoading(false);
      }
    }, 600);

    return () => clearTimeout(timer);
  }, [
    tagline,
    heading,
    xUrl,
    linkedinUrl,
    facebookUrl,
    instagramUrl,
    headerBgPreview,
    bodyBgPreview,
    footerBgPreview,
    headerBannerPreview,
    footerBannerPreview,
    logoIconPreview,
    user?.accessToken,
  ]);

  const fetchSettings = async () => {
    try {
      Loading.circle('Loading email branding...');
      const { data } = await axios.get(`${ENDPOINT}api/v1/admin/email-branding`, {
        headers: { Authorization: `Bearer ${user?.accessToken}` },
      });
      reset(data.data);
      setHeaderBgPreview(data.data.headerBgImage || '');
      setBodyBgPreview(data.data.bodyBgImage || '');
      setFooterBgPreview(data.data.footerBgImage || '');
      setHeaderBannerPreview(data.data.headerBannerImage || '');
      setFooterBannerPreview(data.data.footerBannerImage || '');
      setLogoIconPreview(data.data.logoIcon || '');
    } catch (error) {
      console.error(error);
      Notify.failure('Failed to fetch email branding settings');
    } finally {
      Loading.remove();
    }
  };

  const handleSave = async (formValues) => {
    try {
      Loading.circle('Saving...');

      const formData = new FormData();
      Object.entries(formValues).forEach(([key, value]) => {
        formData.append(key, value ?? '');
      });
      if (headerBgFile) formData.append('headerBgImage', headerBgFile);
      if (bodyBgFile) formData.append('bodyBgImage', bodyBgFile);
      if (footerBgFile) formData.append('footerBgImage', footerBgFile);
      if (headerBgRemoved) formData.append('removeHeaderBgImage', 'true');
      if (bodyBgRemoved) formData.append('removeBodyBgImage', 'true');
      if (footerBgRemoved) formData.append('removeFooterBgImage', 'true');
      if (headerBannerFile) formData.append('headerBannerImage', headerBannerFile);
      if (footerBannerFile) formData.append('footerBannerImage', footerBannerFile);
      if (headerBannerRemoved) formData.append('removeHeaderBannerImage', 'true');
      if (footerBannerRemoved) formData.append('removeFooterBannerImage', 'true');
      if (logoIconFile) formData.append('logoIcon', logoIconFile);
      if (logoIconRemoved) formData.append('removeLogoIcon', 'true');

      // Let axios set the multipart Content-Type (with boundary) itself — setting it
      // manually here would omit the boundary and break the upload.
      const { data } = await axios.put(`${ENDPOINT}api/v1/admin/email-branding`, formData, {
        headers: { Authorization: `Bearer ${user?.accessToken}` },
      });

      // Re-sync local state to the saved result — real hosted URLs now, not the
      // local file/data-URL previews used before saving.
      setHeaderBgFile(null);
      setBodyBgFile(null);
      setFooterBgFile(null);
      setHeaderBgRemoved(false);
      setBodyBgRemoved(false);
      setFooterBgRemoved(false);
      setHeaderBgPreview(data.data.headerBgImage || '');
      setBodyBgPreview(data.data.bodyBgImage || '');
      setFooterBgPreview(data.data.footerBgImage || '');
      setHeaderBannerFile(null);
      setFooterBannerFile(null);
      setHeaderBannerRemoved(false);
      setFooterBannerRemoved(false);
      setHeaderBannerPreview(data.data.headerBannerImage || '');
      setFooterBannerPreview(data.data.footerBannerImage || '');
      setLogoIconFile(null);
      setLogoIconRemoved(false);
      setLogoIconPreview(data.data.logoIcon || '');

      Notify.success('Email branding updated — every email will use these values going forward');
    } catch (error) {
      console.error(error);
      Notify.failure('Failed to update email branding settings');
    } finally {
      Loading.remove();
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={4} mb={4}>
        <MDButton
          variant="text"
          color="dark"
          onClick={() => navigate('/email-templates')}
          sx={{ mb: 2 }}
        >
          <Icon sx={{ mr: 0.5 }}>arrow_back</Icon>
          Back to Email Templates
        </MDButton>
        <Grid container spacing={3}>
          <Grid item xs={12} lg={7}>
            <Card sx={{ p: 3 }}>
              <MDTypography variant="h5" mb={1}>
                Email Branding
              </MDTypography>
              <MDTypography variant="caption" color="secondary">
                Controls the header tagline and footer social links shown on every email —
                previously hardcoded, now editable here without a code change.
              </MDTypography>
              <Divider sx={{ my: 2 }} />

              <form onSubmit={handleSubmit(handleSave)}>
                <MDTypography variant="button" fontWeight="medium" display="block" mb={1}>
                  Header
                </MDTypography>

                <BackgroundImageField
                  label="Logo icon"
                  hint="Shown inside the circular badge, next to the heading. Falls back to the default Desteliq icon when unset."
                  previewUrl={logoIconPreview}
                  onFileSelect={(file) => selectBgImage(file, setLogoIconFile, setLogoIconPreview, setLogoIconRemoved)}
                  onRemove={() => removeBgImage(setLogoIconFile, setLogoIconPreview, setLogoIconRemoved)}
                />

                <Controller
                  name="heading"
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Heading"
                      fullWidth
                      margin="normal"
                      error={!!fieldState.error}
                      helperText={
                        fieldState.error?.message ||
                        'Shown next to the logo icon (a blue "." is always appended after it)'
                      }
                    />
                  )}
                />

                <Controller
                  name="tagline"
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Header tagline"
                      fullWidth
                      margin="normal"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message || 'Shown to the right of the logo and heading'}
                    />
                  )}
                />

                <Divider sx={{ my: 2 }} />
                <MDTypography variant="button" fontWeight="medium" display="block" mb={1}>
                  Footer social links
                </MDTypography>

                <Controller
                  name="xUrl"
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      value={field.value || ''}
                      label="X (Twitter) URL"
                      fullWidth
                      margin="normal"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
                <Controller
                  name="linkedinUrl"
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      value={field.value || ''}
                      label="LinkedIn URL"
                      fullWidth
                      margin="normal"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
                <Controller
                  name="facebookUrl"
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      value={field.value || ''}
                      label="Facebook URL"
                      fullWidth
                      margin="normal"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
                <Controller
                  name="instagramUrl"
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      value={field.value || ''}
                      label="Instagram URL"
                      fullWidth
                      margin="normal"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />

                <Divider sx={{ my: 2 }} />
                <MDTypography variant="button" fontWeight="medium" display="block" mb={1}>
                  Background images
                </MDTypography>
                <MDTypography variant="caption" color="secondary" display="block" mb={2}>
                  Optional — each falls back to the current coded design (gradient header, white
                  body, solid footer color) when no image is set.
                </MDTypography>

                <BackgroundImageField
                  label="Header background image"
                  hint="Shown behind the logo and tagline, replacing the gradient."
                  previewUrl={headerBgPreview}
                  onFileSelect={(file) =>
                    selectBgImage(file, setHeaderBgFile, setHeaderBgPreview, setHeaderBgRemoved)
                  }
                  onRemove={() => removeBgImage(setHeaderBgFile, setHeaderBgPreview, setHeaderBgRemoved)}
                />
                <BackgroundImageField
                  label="Body background image"
                  hint="Shown in the margins around the white content card."
                  previewUrl={bodyBgPreview}
                  onFileSelect={(file) =>
                    selectBgImage(file, setBodyBgFile, setBodyBgPreview, setBodyBgRemoved)
                  }
                  onRemove={() => removeBgImage(setBodyBgFile, setBodyBgPreview, setBodyBgRemoved)}
                />
                <BackgroundImageField
                  label="Footer background image"
                  hint="Shown behind the footer links and social icons, replacing the solid color."
                  previewUrl={footerBgPreview}
                  onFileSelect={(file) =>
                    selectBgImage(file, setFooterBgFile, setFooterBgPreview, setFooterBgRemoved)
                  }
                  onRemove={() => removeBgImage(setFooterBgFile, setFooterBgPreview, setFooterBgRemoved)}
                />

                <Divider sx={{ my: 2 }} />
                <MDTypography variant="button" fontWeight="medium" display="block" mb={1}>
                  Banner images
                </MDTypography>
                <MDTypography variant="caption" color="secondary" display="block" mb={2}>
                  Optional — a full-width image section. Hidden entirely (no gap left behind)
                  when no image is set.
                </MDTypography>

                <BackgroundImageField
                  label="Header banner"
                  hint="Shown as its own full-width section right after the header."
                  previewUrl={headerBannerPreview}
                  onFileSelect={(file) =>
                    selectBgImage(file, setHeaderBannerFile, setHeaderBannerPreview, setHeaderBannerRemoved)
                  }
                  onRemove={() =>
                    removeBgImage(setHeaderBannerFile, setHeaderBannerPreview, setHeaderBannerRemoved)
                  }
                />
                <BackgroundImageField
                  label="Footer banner"
                  hint="Shown as its own full-width section at the end of the body, before the footer."
                  previewUrl={footerBannerPreview}
                  onFileSelect={(file) =>
                    selectBgImage(file, setFooterBannerFile, setFooterBannerPreview, setFooterBannerRemoved)
                  }
                  onRemove={() =>
                    removeBgImage(setFooterBannerFile, setFooterBannerPreview, setFooterBannerRemoved)
                  }
                />

                <MDBox mt={3}>
                  <MDButton type="submit" variant="gradient" color="info" fullWidth disabled={isSubmitting}>
                    {isSubmitting ? 'Saving...' : 'Save Email Branding'}
                  </MDButton>
                </MDBox>
              </form>
            </Card>
          </Grid>

          <Grid item xs={12} lg={5}>
            <Card sx={{ p: 3 }}>
              <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <MDTypography variant="h6">Live preview</MDTypography>
                {previewLoading && (
                  <MDTypography variant="caption" color="secondary">
                    Rendering…
                  </MDTypography>
                )}
              </MDBox>
              <MDTypography variant="caption" color="secondary" display="block" mb={2}>
                Shows your changes below immediately, even before you hit Save.
              </MDTypography>
              {previewHtml ? (
                <iframe
                  title="Email branding preview"
                  srcDoc={previewHtml}
                  style={{
                    width: '100%',
                    height: 600,
                    border: '1px solid #e0e0e0',
                    borderRadius: 8,
                  }}
                />
              ) : (
                <MDBox
                  sx={{
                    width: '100%',
                    height: 600,
                    border: '1px dashed #ccc',
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'text.secondary',
                    fontSize: 13,
                  }}
                >
                  Loading preview…
                </MDBox>
              )}
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
};

export default EmailBranding;
